import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AdjustIcon from "@mui/icons-material/Adjust";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbIcon from "@mui/icons-material/Lightbulb";

/* ---------------- HELPERS ---------------- */

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCr = (val) => {
  const num = Number(val || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} CR`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const getProjectGraphData = (rows = []) => {
  const map = {};
  rows.forEach((row) => {
    const name = row?.projectName || row?.Projects?.projectName || "Unknown";
    if (!map[name]) map[name] = { project: name, value: 0, brokerage: 0, units: 0 };
    map[name].value += Number(row.booking_value || 0);
    map[name].brokerage += Number(row.base_brokerage_amount || 0);
    map[name].units += 1;
  });
  return Object.values(map);
};

    const getShortProjectName = (name = "") => {
        if (!name) return "-";

        const words = name.trim().split(" ");

        // 1 word → return first 6 chars
        if (words.length === 1) return words[0].slice(0, 6);

        // 2 words → first 3 + first 3
        if (words.length === 2)
            return `${words[0].slice(0, 3)} ${words[1].slice(0, 3)}`;

        // 3+ words → initials (best UX)
        return words.map((w) => w[0]).join("").toUpperCase();
    };

const downloadCSV = (rows, name) => {
  if (!rows?.length) return;
  const csv = [
    ["Project", "Value", "Brokerage", "Date"],
    ...rows.map((r) => [
      r.projectName,
      r.booking_value,
      r.base_brokerage_amount,
      formatDate(r.created_at),
    ]),
  ]
    .map((r) => r.join(","))
    .join("\n");
  const blob = new Blob([csv]);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${name}.csv`;
  link.click();
};

const CHART_COLORS = ["#3B4FE8", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6"];

/* ---------------- SUB COMPONENTS ---------------- */

const StatCard = ({ title, value, sub, subColor, icon, iconBg }) => (
  <Card
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      border: "1px solid #EAECF4",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: iconBg || "#F0F4FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography fontSize={11} color="#8A94A6" fontWeight={600} letterSpacing={0.5}>
        {title}
      </Typography>
    </Box>
    <Typography fontWeight={700} fontSize={{ xs: 18, sm: 22 }} color="#111827" lineHeight={1.2}>
      {value}
    </Typography>
    {sub && (
      <Typography fontSize={11} color={subColor || "#6B7280"} mt={0.3}>
        {sub}
      </Typography>
    )}
  </Card>
);

/* Milestone step node */
const MilestoneNode = ({ rate, label, sublabel, active, completed, isLast }) => (
  <Box
    flex={1}
    display="flex"
    flexDirection="column"
    alignItems="center"
    position="relative"
    sx={{ minWidth: 0 }}
  >
    <Typography
      fontSize={11}
      fontWeight={700}
      color={active ? "#4F46E5" : completed ? "#4F46E5" : "#9CA3AF"}
      mb={0.5}
    >
      {rate}
    </Typography>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: completed
          ? "linear-gradient(135deg, #4F46E5, #7C3AED)"
          : active
          ? "linear-gradient(135deg, #4F46E5, #7C3AED)"
          : "#E5E7EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: active ? "3px solid #A5B4FC" : "none",
        zIndex: 1,
        boxShadow: active ? "0 0 0 4px rgba(79,70,229,0.15)" : "none",
      }}
    >
      {completed ? (
        <CheckCircleIcon sx={{ fontSize: 16, color: "#fff" }} />
      ) : active ? (
        <AdjustIcon sx={{ fontSize: 16, color: "#fff" }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
      )}
    </Box>
    <Typography fontSize={10} color="#6B7280" mt={0.5} textAlign="center">
      {label}
    </Typography>
    {sublabel && (
      <Typography fontSize={10} fontWeight={600} color={active ? "#4F46E5" : "#9CA3AF"} textAlign="center">
        {sublabel}
      </Typography>
    )}
  </Box>
);

/* ---------------- MAIN COMPONENT ---------------- */

// Derive short FY label "FY YY-YY" from a unix timestamp
const timestampToFY = (ts) => {
  if (!ts) return null;
  const d = new Date(Number(ts));
  if (isNaN(d)) return null;
  const yr = d.getFullYear();
  const mo = d.getMonth() + 1;
  const s = mo >= 4 ? yr : yr - 1;
  return `FY ${String(s).slice(2)}-${String(s + 1).slice(2)}`;
};

// Normalize any FY string ("FY 2025-26", "FY 25-26") to short form
const normalizeFY = (fy) => {
  if (!fy) return null;
  const m = fy.match(/(\d{2,4})-(\d{2,4})/);
  if (!m) return fy;
  let [, a, b] = m;
  if (a.length === 4) a = a.slice(2);
  if (b.length === 4) b = b.slice(2);
  return `FY ${a}-${b}`;
};

const getSlabLabel = (slab) => {
  if (slab?.percentage != null) return `${Number(slab.percentage).toFixed(0)}%`;
  if (slab?.amount != null) return `₹${Number(slab.amount).toLocaleString("en-IN")}`;
  return "-";
};

const BaseBrokerageDetailsPage = ({ selectedPlan, onBack, aopPlansData = [] }) => {
  const rows = selectedPlan?.rows || [];
  const projectData = useMemo(() => getProjectGraphData(rows), [rows]);

  const maxValue = Math.max(...projectData.map((p) => p.value), 1);
  const totalBrokerage = projectData.reduce((a, b) => a + b.brokerage, 0);

  const getDonutSegments = () => {
    let cumulative = 0;
    return projectData.map((p, i) => {
      const percent = totalBrokerage > 0 ? (p.brokerage / totalBrokerage) * 100 : 0;
      const start = cumulative;
      cumulative += percent;
      return { color: CHART_COLORS[i % CHART_COLORS.length], start, end: cumulative, percent };
    });
  };

  const donutSegments = getDonutSegments();
  const donutGradient = donutSegments
    .map((s) => `${s.color} ${s.start}% ${s.end}%`)
    .join(", ");

  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  if (!selectedPlan) return null;

  const { fy = "", stats = {} } = selectedPlan;
  const totalUnits = stats.totalUnits || 0;
  const totalValue = stats.totalValue || 0;
  const received = stats.received || 0;
  const pending = stats.pending || 0;

  const percent = totalUnits;

  // ── AOP plan for this FY ──────────────────────────────────────────────────
  const baseFY = fy;
  const matchingAOP = aopPlansData.find(
    (a) => normalizeFY(a.plan?.financial_year) === baseFY
  );
  const aopFirstSlab = matchingAOP?.slabs?.[0] || null;
  const aopFirstSlabTarget = aopFirstSlab ? Number(aopFirstSlab.from_value || 0) : 0;
  const aopCurrentValue = matchingAOP?.stats?.totalValue || 0;
  const aopToFirstSlab = Math.max(aopFirstSlabTarget - aopCurrentValue, 0);
  const aopSlabLabel = aopFirstSlab ? getSlabLabel(aopFirstSlab) : null;
  const aopHasData = !!matchingAOP && !!aopFirstSlab;

  // Project-wise aggregated rows
  const projectBreakdownRows = projectData;

  return (
    <Box sx={{
        height: { xs: "80vh", md: "90vh" },
      overflowY: "auto",
      overflowX: "hidden",
      background: "#F4F5FA",
    }}>
      {/* ── TOP HEADER ── */}
      <Box
        sx={{
          background: "#fff",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: "1px solid #EAECF4",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }} onClick={onBack}>
          <ArrowBackIcon sx={{ fontSize: 20, color: "#374151" }} />
          <Box>
            <Typography fontWeight={700} fontSize={{ xs: 15, sm: 18 }} color="#111827" lineHeight={1.2}>
              {fy || "Basic Brokerage"}
            </Typography>
            <Typography fontSize={{ xs: 10, sm: 11 }} color="#B45309" mt={0.4}>
              Basic Brokerage
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={() => downloadCSV(rows, fy)}
          sx={{
            textTransform: "none",
            borderRadius: 2.5,
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 600,
            color: "#374151",
            borderColor: "#D1D5DB",
            px: 2,
            py: 0.8,
            "&:hover": { borderColor: "#9CA3AF", background: "#F9FAFB" },
          }}
        >
          Download Excel
        </Button>
      </Box>

      <Box sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        px: { xs: 2, sm: 2.5 },
        py: 2.5,
        pb: { xs: 14, sm: 15 },
      }}>
        {/* ── OVERALL ACHIEVEMENT BANNER ── */}
        <Box
          sx={{
            p: { xs: "14px 16px", sm: "20px 24px" },
            borderRadius: 3,
            background: "linear-gradient(100deg, #3730A3 0%, #4F46E5 45%, #7C3AED 100%)",
            color: "#fff",
            mb: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              top: -60,
              right: 200,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              bottom: -40,
              right: 80,
            }}
          />

          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography fontSize={11} fontWeight={600} letterSpacing={1.2} sx={{ opacity: 0.8 }}>
                BASIC BROKERAGE · {fy}
              </Typography>
              <Typography fontSize={{ xs: 22, sm: 30 }} fontWeight={800} lineHeight={1.1} mt={0.3}>
                {formatCr(stats.totalBrokerage || 0)}
              </Typography>
              <Typography fontSize={{ xs: 12, sm: 13 }} fontWeight={400} sx={{ opacity: 0.85 }} mt={0.3}>
                total brokerage earned
              </Typography>
            </Box>

            {/* Current Rate badge */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 2.5,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.8, sm: 1 },
                textAlign: "right",
                flexShrink: 0,
              }}
            >
              <Typography fontSize={10} sx={{ opacity: 0.8 }} letterSpacing={0.8}>
                sold units
              </Typography>
              <Typography fontSize={{ xs: 18, sm: 22 }} fontWeight={800}>
                {totalUnits}
              </Typography>
            </Box>
          </Box>

        </Box>

        {/* ── STAT CARDS ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 2,
            width: "100%",
          }}
        >
          <StatCard
            title="SOLD UNITS"
            value={totalUnits}
            icon={<HomeWorkIcon sx={{ fontSize: 16, color: "#4F46E5" }} />}
            iconBg="#EEF2FF"
          />
          <StatCard
            title="SOLD VALUE"
            value={formatCr(totalValue)}
            icon={<TrendingUpIcon sx={{ fontSize: 16, color: "#10B981" }} />}
            iconBg="#ECFDF5"
          />
          <StatCard
            title="BROKERAGE"
            value={formatCr(totalBrokerage)}
            subColor="#10B981"
            icon={<AccountBalanceWalletIcon sx={{ fontSize: 16, color: "#10B981" }} />}
            iconBg="#ECFDF5"
          />
          <StatCard
            title="PENDING"
            value={formatCr(pending)}
            sub="Pending agreement"
            subColor="#F59E0B"
            icon={<PendingActionsIcon sx={{ fontSize: 16, color: "#F59E0B" }} />}
            iconBg="#FFFBEB"
          />
        </Box>

        {/* ── WHAT'S NEXT BANNER ── */}
        {aopHasData ? (
          <Box
            sx={{
              background: "#FFFBEB",
              border: "1px solid #FDE68A",
              borderRadius: 3,
              px: 2,
              py: 2,
              mb: 2,
              mt: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "#FEF3C7",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <LightbulbIcon sx={{ fontSize: 18, color: "#D97706" }} />
              </Box>
              <Box>
                <Typography fontWeight={700} fontSize={13} color="#92400E">
                  What's Next? — {baseFY} AOP Target
                </Typography>
                {aopToFirstSlab > 0 ? (
                  <Typography fontSize={12} color="#78350F">
                    Sell{" "}
                    <Typography component="span" fontWeight={700} color="#D97706">
                      {formatCr(aopToFirstSlab)}
                    </Typography>{" "}
                    more to unlock the{" "}
                    <Typography component="span" fontWeight={700} color="#D97706">
                      {aopSlabLabel}
                    </Typography>{" "}
                    AOP slab (target {formatCr(aopFirstSlabTarget)})
                  </Typography>
                ) : (
                  <Typography fontSize={12} color="#15803D" fontWeight={600}>
                    🎉 First AOP slab unlocked! ({aopSlabLabel})
                  </Typography>
                )}
                <Typography fontSize={11} color="#92400E" mt={0.3}>
                  Current AOP value: {formatCr(aopCurrentValue)} / {formatCr(aopFirstSlabTarget)}
                </Typography>
              </Box>
            </Box>

            <Box textAlign={{ xs: "left", sm: "right" }}>
              <Typography fontSize={10} color="#92400E" letterSpacing={0.8} fontWeight={600}>
                FIRST SLAB
              </Typography>
              <Typography fontSize={18} fontWeight={800} color="#D97706">
                {aopSlabLabel}
              </Typography>
              <Typography fontSize={10} color="#B45309">
                @ {formatCr(aopFirstSlabTarget)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              background: "#F9FAFB", border: "1px solid #E5E7EB",
              borderRadius: 3, px: 2, py: 1.8, mb: 2, mt: 1,
              display: "flex", alignItems: "center", gap: 1.5,
            }}
          >
            <LightbulbIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
            <Typography fontSize={13} color="#9CA3AF">
              No AOP plan found for {baseFY || "this year"}.
            </Typography>
          </Box>
        )}

      

        {/* ── CHARTS ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={2}>
          {/* Bar Chart */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #EAECF4",
                p: { xs: 1.5, sm: 2.5 },
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}
            >
              <Typography fontWeight={700} fontSize={14} color="#111827" mb={2}>
                Project-wise Sold Value (CR)
              </Typography>

              <Box sx={{ overflowX: "auto", pb: 0.5 }}>
              <Box display="flex" alignItems="flex-end" height={180} gap={1.5} px={1} sx={{ minWidth: projectData.length * 72 }}>
                {projectData.map((item, i) => {
                  const heightPct = item.value > 0 ? Math.max((item.value / maxValue) * 100, 8) : 0;
                  return (
                    <Tooltip
                      key={i}
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography fontSize={12} fontWeight={700} color="#fff">
                            {item.project || "—"}
                          </Typography>
                          <Typography fontSize={11} color="rgba(255,255,255,0.8)" mt={0.3}>
                            Sold Value: {formatCr(item.value)}
                          </Typography>
                          <Typography fontSize={11} color="#86EFAC" fontWeight={700} mt={0.2}>
                            Brokerage: {formatCr(item.brokerage)}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box flex={1} display="flex" flexDirection="column" alignItems="center" sx={{ cursor: "pointer" }}>
                        <Typography fontSize={10} color="#6B7280" mb={0.5} fontWeight={600}>
                          {formatCr(item.value)}
                        </Typography>
                        <Box
                          sx={{
                            width: "100%",
                            height: animated ? `${heightPct * 1.6}px` : "0px",
                            background: "#3B4FE8",
                            borderRadius: "6px 6px 0 0",
                            transition: `height 0.7s cubic-bezier(0.4,0,0.2,1) ${i * 0.08}s`,
                            "&:hover": { background: "#4F46E5", filter: "brightness(1.15)" },
                          }}
                        />
                        <Box sx={{ borderTop: "2px solid #E5E7EB", width: "100%" }} />
                        <Typography
                          fontSize={10}
                          mt={0.7}
                          color="#6B7280"
                          textAlign="center"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          {getShortProjectName(item.project)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              </Box>
            </Card>
          </Grid>

          {/* Donut Chart */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #EAECF4",
                p: { xs: 1.5, sm: 2.5 },
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              }}
            >
              <Typography fontWeight={700} fontSize={14} color="#111827" mb={2}>
                Brokerage Distribution
              </Typography>

              {totalBrokerage === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center" height={180}>
                  <Typography color="#9CA3AF">No Data</Typography>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="space-around" flexWrap="wrap" gap={2} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
                  <Box
                    sx={{
                      width: { xs: 130, sm: 160 },
                      height: { xs: 130, sm: 160 },
                      borderRadius: "50%",
                      background: `conic-gradient(${donutGradient})`,
                      position: "relative",
                      flexShrink: 0,
                      animation: "donutFadeIn 0.7s cubic-bezier(0.4,0,0.2,1) both",
                      "@keyframes donutFadeIn": {
                        from: { opacity: 0, transform: "scale(0.6) rotate(-90deg)" },
                        to: { opacity: 1, transform: "scale(1) rotate(0deg)" },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography fontSize={9} color="#6B7280" fontWeight={600} letterSpacing={0.5}>
                        TOTAL
                      </Typography>
                      <Typography fontSize={12} fontWeight={700} color="#111827">
                        {formatCr(totalBrokerage)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Legend */}
                  <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, flexWrap: "wrap", gap: { xs: 0.5, sm: 0 }, justifyContent: "center" }}>
                    {projectData.map((p, i) => (
                      <Tooltip
                        key={i}
                        arrow
                        placement="right"
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography fontSize={12} fontWeight={700} color="#fff">
                              {p.project || "—"}
                            </Typography>
                            <Typography fontSize={11} color="rgba(255,255,255,0.8)" mt={0.3}>
                              Sold Value: {formatCr(p.value)}
                            </Typography>
                            <Typography fontSize={11} color="#86EFAC" fontWeight={700} mt={0.2}>
                              Brokerage: {formatCr(p.brokerage)}
                            </Typography>
                            <Typography fontSize={11} color="rgba(255,255,255,0.7)" mt={0.2}>
                              Share: {donutSegments[i]?.percent.toFixed(1)}%
                            </Typography>
                          </Box>
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.8}
                          sx={{
                            cursor: "pointer",
                            px: 0.8,
                            py: 0.4,
                            borderRadius: 1.5,
                            transition: "background 0.2s",
                            "&:hover": { background: "rgba(0,0,0,0.05)" },
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: CHART_COLORS[i % CHART_COLORS.length],
                              flexShrink: 0,
                            }}
                          />
                          <Typography fontSize={{ xs: 10, sm: 11 }} color="#374151" noWrap>
                            {getShortProjectName(p.project)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* ── PROJECT-WISE BREAKDOWN TABLE ── */}
        <Card
          sx={{
            borderRadius: 3,
            border: "1px solid #EAECF4",
            mb: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            px={{ xs: 1.5, sm: 2.5 }}
            py={1.5}
            display="flex"
            alignItems="center"
            gap={1}
            borderBottom="1px solid #F3F4F6"
          >
            <HomeWorkIcon sx={{ fontSize: 16, color: "#4F46E5" }} />
            <Typography fontWeight={700} fontSize={{ xs: 13, sm: 14 }} color="#111827">
              Project-wise Breakdown
            </Typography>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#F9FAFB" }}>
                {["PROJECT", "UNITS", "SOLD VALUE", "BROKERAGE"].map((h) => (
                  <TableCell key={h} sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 700, color: "#6B7280", letterSpacing: 0.5, py: 1, whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {projectBreakdownRows.map((row, i) => (
                <TableRow
                  key={i}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { background: "#F9FAFB" },
                  }}
                >
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, color: "#111827", fontWeight: 500, py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {getShortProjectName(row.project)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, color: "#374151", py: { xs: 1, sm: 1.5 } }}>
                    {row.units}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, color: "#374151", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {formatCr(row.value)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, fontWeight: 600, color: "#10B981", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {formatCr(row.brokerage)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </Card>

        {/* ── BOOKING-WISE BREAKDOWN TABLE ── */}
        <Card
          sx={{
            borderRadius: 3,
            border: "1px solid #EAECF4",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            px={{ xs: 1.5, sm: 2.5 }}
            py={1.5}
            display="flex"
            alignItems="center"
            gap={1}
            borderBottom="1px solid #F3F4F6"
          >
            <AccountBalanceWalletIcon sx={{ fontSize: 16, color: "#4F46E5" }} />
            <Typography fontWeight={700} fontSize={{ xs: 13, sm: 14 }} color="#111827">
              Booking-wise Breakdown
            </Typography>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#F9FAFB" }}>
                {["PROJECT", "SOLD VALUE", "BROKERAGE", "STATUS", "DATE"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 700, color: "#6B7280", letterSpacing: 0.5, py: 1, whiteSpace: "nowrap" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow
                  key={i}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { background: "#F9FAFB" },
                  }}
                >
                  
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, color: "#111827", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {getShortProjectName(row.projectName)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, color: "#374151", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {formatCr(row.booking_value)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 11, sm: 13 }, fontWeight: 600, color: "#10B981", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {formatCr(row.base_brokerage_amount)}
                  </TableCell>
                  <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                    <Chip
                      label={row.status || "Unknown"}
                      size="small"
                      sx={{
                        fontSize: { xs: 9, sm: 11 },
                        fontWeight: 600,
                        height: { xs: 18, sm: 22 },
                        backgroundColor:
                          row.status === "Paid" ? "#DCFCE7"
                          : row.status === "Pending" ? "#FEF3C7"
                          : "#F3F4F6",
                        color:
                          row.status === "Paid" ? "#15803D"
                          : row.status === "Pending" ? "#B45309"
                          : "#6B7280",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 10, sm: 12 }, color: "#6B7280", py: { xs: 1, sm: 1.5 }, whiteSpace: "nowrap" }}>
                    {formatDate(row.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default BaseBrokerageDetailsPage;